// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type Holding } from '@shared/data-access/interfaces/api/ai/holding';
import { isDefined } from '@shared/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { groupBy } from '@shared/data-access/utils/groupby';
import type {
  EtfAnalysis,
  MutualFundAnalysis,
  StockAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { portfolioApi } from '@shared/data-access/api/ai/portfolio-api';
import { useSafeUpdate } from '@shared/ui-kit/hooks/use-safe-update';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';

interface AssetAnalysisResp {
  stock: StockAnalysis | null | any;
  etf: EtfAnalysis | null;
  fund: MutualFundAnalysis | null;
}

export interface UseAllocationAssetAnalysisProps {
  holdings: Pick<
    AccountsHoldingsData,
    'asset_type' | 'details' | 'ticker' | 'isin' | 'price' | 'quantity'
  >[];
  hist: number;
  benchmark: Array<{ ticker: string; weight: number }>;
}

export const useAllocationAssetAnalysis = ({
  hist,
  holdings: positions,
  benchmark,
}: UseAllocationAssetAnalysisProps) => {
  const runSafeUpdate = useSafeUpdate();

  const [getAnalysis, analysisQuery] = portfolioApi.useLazyPortfolioAnalysisQuery();

  const [assetAnalysis, setAssetAnalysis] = useState<AssetAnalysisResp>({
    stock: null,
    etf: null,
    fund: null,
  });

  const groupedPositions = useMemo(
    () => groupBy(positions, 'asset_type'),
    [positions],
  );

  const getAnalysisData = useCallback(async () => {
    try {
      for (const [assetType, positions] of groupedPositions.entries()) {
        /* istanbul ignore next */
        const filteredPositions = positions
          .filter(({ details }) => isDefined(details))
          .map(
            (position): Holding => ({
              ticker:
                position.details?.ticker ??
                position.details?.isin ??
                position.ticker ??
                position.isin!,
              price: position.price,
              qty: position.quantity,
              unrealized_capital_gains: 0,
            }),
          );

        /* istanbul ignore if */
        if (filteredPositions.length === 0) continue;

        const mapAssetType: Record<string, string> = {
          stock: 'stock',
          etf: 'etf',
          fund: 'mutual_fund',
          'pension-plan': 'mutual_fund',
        };

        const payload = {
          benchmark: {
            target_asset: benchmark,
          },
          holdings: filteredPositions,
        };

        const { data: analysisData } = await getAnalysis(
          {
            payload,
            params: { hist_w: hist },
            assetType: mapAssetType[assetType],
          },
          true,
        );
        /* istanbul ignore next */
        runSafeUpdate(() => {
          setAssetAnalysis(prevState => ({
            ...prevState,
            [assetType]: analysisData,
          }));
        });
      }
    } catch (error) {
      /* istanbul ignore next */
      console.error('[useAggregatorAllocationAssetAnalysis]:', error);
    }
  }, [hist, benchmark, getAnalysis, groupedPositions, runSafeUpdate]);

  useEffect(() => {
    getAnalysisData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hist, benchmark, groupedPositions]);

  return {
    assetAnalysis,
    analysisQuery,
  };
};
